/**
 * @fileoverview Generic popover for setting image URLs with validation.
 * @module shared/components/ui/ImageUrlPopover
 */

import { cloneElement, useState, type ReactElement } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'

type TriggerElement = ReactElement<{ disabled?: boolean }>

/**
 * Props for {@link ImageUrlPopover}.
 */
export interface ImageUrlPopoverProps {
  /** Element used to open the image setting popover. */
  trigger: TriggerElement
  /** Current image URL */
  currentImageUrl: string | null | undefined
  /** Title displayed in the popover header */
  title?: string
  /** Description text shown below the title */
  description?: string
  /** Aria label for the input field */
  inputAriaLabel?: string
  /** Custom save handler that receives the new image URL (null if clearing) */
  onSave: (imageUrl: string | null) => Promise<void>
  /** Invoked after successful save */
  onUpdated?: (() => Promise<unknown> | unknown) | undefined
  /** Success toast message */
  successMessage?: string
  /** Error toast message */
  errorMessage?: string
  /** Disable the trigger entirely */
  disabled?: boolean
}

/**
 * Generic image URL setting popover with validation.
 * Reusable across different entities (SKUs, Product Groups, etc.).
 *
 * @component
 * @example
 * ```tsx
 * <ImageUrlPopover
 *   trigger={<img src={imageUrl} alt="Product" className="cursor-pointer" />}
 *   currentImageUrl={item.imageUrl}
 *   title="Set Product Image"
 *   onSave={async (url) => await updateItem({ imageUrl: url })}
 *   onUpdated={() => void refetch()}
 * />
 * ```
 */
export function ImageUrlPopover({
  trigger,
  currentImageUrl,
  title = 'Set Image URL',
  description = 'Enter the URL of the image',
  inputAriaLabel = 'Image URL',
  onSave,
  onUpdated,
  successMessage = 'Image URL updated successfully',
  errorMessage = 'Failed to update image URL',
  disabled = false,
}: ImageUrlPopoverProps): ReactElement {
  const [open, setOpen] = useState<boolean>(false)
  const [imageUrl, setImageUrl] = useState<string>(currentImageUrl ?? '')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const handleOpenChange = (nextOpen: boolean): void => {
    if (disabled) return
    setOpen(nextOpen)
    // Reset to current value when opening
    if (nextOpen) {
      setImageUrl(currentImageUrl ?? '')
    }
  }

  const handleCancel = (): void => {
    setOpen(false)
    setImageUrl(currentImageUrl ?? '')
  }

  const handleSave = async (): Promise<void> => {
    const trimmedUrl = imageUrl.trim()

    // Validate URL format if not empty
    if (
      trimmedUrl !== '' &&
      !trimmedUrl.startsWith('http://') &&
      !trimmedUrl.startsWith('https://')
    ) {
      toast.error('Image URL must start with http:// or https://')
      return
    }

    setIsSubmitting(true)
    try {
      await onSave(trimmedUrl === '' ? null : trimmedUrl)
      toast.success(successMessage)
      if (onUpdated) {
        await onUpdated()
      }
    } catch (error) {
      console.error('Failed to update image URL:', error)
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
      setOpen(false)
    }
  }

  const triggerElement = cloneElement(trigger, {
    disabled: trigger.props.disabled ?? disabled,
  })

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{triggerElement}</PopoverTrigger>
      <PopoverContent align="end" className="w-[20rem] space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <div className="space-y-2">
          <Input
            type="url"
            placeholder="https://example.com/image.jpg"
            value={imageUrl}
            onChange={e => setImageUrl(e.target.value)}
            disabled={isSubmitting}
            aria-label={inputAriaLabel}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => {
              void handleSave()
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default ImageUrlPopover
